'use client'

import { useEffect, useRef, useState } from 'react'

function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(element)
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, isVisible }
}

export function AnimatedSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const { ref, isVisible } = useScrollAnimation()

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}ms`,
      }}>
      {children}
    </div>
  )
}

export function AnimatedItem({
  children,
  className = '',
  index = 0,
}: {
  children: React.ReactNode
  className?: string
  index?: number
}) {
  const { ref, isVisible } = useScrollAnimation(0.05)

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transitionDelay: `${index * 100}ms`,
      }}>
      {children}
    </div>
  )
}

export function TimelineBar({
  label,
  color,
  startCol,
  endCol,
  row,
}: {
  label: string
  color: string
  startCol: number
  endCol: number
  row: number
}) {
  const { ref, isVisible } = useScrollAnimation(0.1)
  const span = endCol - startCol + 1

  return (
    <div
      ref={ref}
      className="flex h-10 items-center justify-center overflow-hidden rounded-lg text-sm font-medium text-white"
      style={{
        gridColumn: `${startCol} / span ${span}`,
        gridRow: row,
        backgroundColor: color,
        width: isVisible ? '100%' : '0%',
        transition: 'width 1s ease-out',
        transitionDelay: `${(row - 2) * 150}ms`,
      }}>
      <span
        style={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease-out',
          transitionDelay: `${(row - 2) * 150 + 800}ms`,
        }}>
        {label}
      </span>
    </div>
  )
}
